import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight, RefreshCw, X, Ban, RotateCcw, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import FilterPills from "./admin/FilterPills";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

// POs admin: manifest lines grouped by supplier + po_number, drilled
// supplier → PO → lines. Each step is a table; clicking a row opens the
// next step. The trail so far is rendered as pills at the top
// ([Suppliers] › [Amazon] › [2026/08/22]) and clicking an earlier pill
// jumps back to that step. Read paths hit /manifest/po/*; the PO-level
// actions (cancel / reopen / close short) POST to /manifest/po/status and
// rewrite every line on that PO, so the Manifest page reflects the change
// immediately.
//
// Navigation state lives in the URL (?supplier=…&po=…) so the browser back
// button also walks back up the drill and a PO view can be deep-linked. An
// empty-string param is meaningful: it selects the lines whose supplier /
// PO column is NULL, which the BE reports as null and the FE labels
// "No supplier" / "No PO number".

type PoStatus = "open" | "fulfilled" | "cancelled" | "archived";

interface SupplierRow {
    supplier_name: string | null;
    total_pos: number;
    active_pos: number;
    open_lines: number;
    expected_qty: number;
    received_qty: number;
    updated_at: string | null;
}

interface PoRow {
    supplier_name: string | null;
    po_number: string | null;
    lines: number;
    open_lines: number;
    fulfilled_lines: number;
    cancelled_lines: number;
    archived_lines: number;
    complete_lines: number;
    expected_qty: number;
    received_qty: number;
    remaining_qty: number;
    manifest_date: string | null;
    expected_at: string | null;
    updated_at: string | null;
    status: PoStatus;
}

interface LineRow {
    manifest_id: number;
    sku: string;
    warehouse: string;
    expected_qty: number;
    remaining_qty: number;
    received_qty: number;
    unit_cost: number;
    currency: string;
    po_number: string | null;
    supplier_name: string | null;
    manifest_date: string | null;
    expected_at: string | null;
    status: PoStatus;
}

type PoAction = "cancel" | "reopen" | "close_short";
type LineFilter = "" | "outstanding" | "complete";

const formatMoney = (value: number, currency = "GBP") => {
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value); }
    catch { return `${currency} ${value.toFixed(2)}`; }
};

const formatDate = (iso: string) => {
    try { return new Date(iso).toLocaleDateString(); }
    catch { return iso; }
};

const pct = (received: number, expected: number) =>
    expected > 0 ? Math.min(100, Math.round((received / expected) * 100)) : 0;

const StatusPill = ({ status }: { status: PoStatus }) => {
    const { t } = useTranslation();
    return (
        <span className={`po-admin-pill po-admin-pill--${status}`}>
            {t(`manifest_status_${status}`)}
        </span>
    );
};

const Progress = ({ received, expected }: { received: number; expected: number }) => {
    const p = pct(received, expected);
    const full = expected > 0 && received >= expected;
    return (
        <div className="po-admin-progress" title={`${received} / ${expected}`}>
            <div className="po-admin-progress-track">
                <div
                    className={`po-admin-progress-fill${full ? " po-admin-progress-fill--full" : ""}`}
                    style={{ width: `${p}%` }}
                />
            </div>
            <span className="po-admin-progress-pct">{p}%</span>
        </div>
    );
};

// One step in the trail. Shares the filter-pill look used by the other
// admin pages; the current step is the highlighted pill, earlier steps
// are plain pills that jump back.
const StepPill = ({ label, current, onClick }: { label: string; current: boolean; onClick: () => void }) => (
    <button
        type="button"
        className={`filter-pill${current ? " filter-pill--active" : ""}`}
        aria-current={current ? "step" : undefined}
        onClick={onClick}
        disabled={current}
    >
        {label}
    </button>
);

const PosAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [params, setParams] = useSearchParams();
    // `has` distinguishes "param absent" (stay on the suppliers step) from
    // "param empty" (the NULL-supplier / NULL-PO bucket).
    const supplierSelected = params.has("supplier");
    const supplierKey = params.get("supplier") ?? "";
    const supplier: string | null = supplierKey === "" ? null : supplierKey;
    const poSelected = supplierSelected && params.has("po");
    const poKey = params.get("po") ?? "";
    const poNumber: string | null = poKey === "" ? null : poKey;
    const showAll = params.get("all") === "1";

    const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
    const [pos, setPos] = useState<PoRow[]>([]);
    const [po, setPo] = useState<PoRow | null>(null);
    const [lines, setLines] = useState<LineRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [lineFilter, setLineFilter] = useState<LineFilter>("");
    const [lineSearch, setLineSearch] = useState("");
    const [pendingAction, setPendingAction] = useState<PoAction | null>(null);
    const [acting, setActing] = useState(false);

    const supplierLabel = (name: string | null) => name ?? t("pos_no_supplier");
    const poLabel = (name: string | null) => name ?? t("pos_no_po");

    const goSuppliers = () => setParams({});
    const goSupplier = (name: string | null, keepAll = showAll) => {
        const next: Record<string, string> = { supplier: name ?? "" };
        if (keepAll) next.all = "1";
        setParams(next);
    };
    const goPo = (supplierName: string | null, po_number: string | null) => {
        const next: Record<string, string> = { supplier: supplierName ?? "", po: po_number ?? "" };
        if (showAll) next.all = "1";
        setParams(next);
    };

    const loadSuppliers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/manifest/po/suppliers`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : []);
        } catch {
            toast.error(t("server_error"), { toastId: "pos-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    const loadPos = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams({ supplier: supplierKey });
            if (showAll) q.set("all", "1");
            const res = await apiFetch(`/manifest/po?${q}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setPos(Array.isArray(data.pos) ? data.pos : []);
        } catch {
            toast.error(t("server_error"), { toastId: "pos-load-err" });
        } finally {
            setLoading(false);
        }
    }, [supplierKey, showAll, t]);

    const loadLines = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams({ supplier: supplierKey, po: poKey });
            const res = await apiFetch(`/manifest/po/lines?${q}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setPo(data.po ?? null);
            setLines(Array.isArray(data.lines) ? data.lines : []);
        } catch {
            toast.error(t("server_error"), { toastId: "pos-load-err" });
        } finally {
            setLoading(false);
        }
    }, [supplierKey, poKey, t]);

    const refresh = useCallback(() => {
        if (poSelected) return loadLines();
        if (supplierSelected) return loadPos();
        return loadSuppliers();
    }, [poSelected, supplierSelected, loadLines, loadPos, loadSuppliers]);

    useEffect(() => { refresh(); }, [refresh]);
    // Reset the line-level filters when moving between POs so a search
    // typed on one PO doesn't blank out the next.
    useEffect(() => { setLineFilter(""); setLineSearch(""); }, [supplierKey, poKey]);

    const visibleLines = useMemo(() => {
        const needle = lineSearch.trim().toLowerCase();
        return lines.filter(l => {
            const complete = l.received_qty >= l.expected_qty;
            if (lineFilter === "complete" && !complete) return false;
            if (lineFilter === "outstanding" && (complete || l.status !== "open")) return false;
            if (needle && !l.sku.toLowerCase().includes(needle)) return false;
            return true;
        });
    }, [lines, lineFilter, lineSearch]);

    // Reopen applies to lines that are neither open nor received in full:
    // cancelled / archived / short-closed with units still outstanding.
    const reopenable = po ? po.lines - po.open_lines - po.complete_lines : 0;
    const actionCount = (action: PoAction) => {
        if (!po) return 0;
        return action === "reopen" ? Math.max(0, reopenable) : po.open_lines;
    };

    const runAction = async () => {
        if (!pendingAction || !po) return;
        setActing(true);
        try {
            const res = await apiFetch(`/manifest/po/status`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    supplier_name: po.supplier_name ?? "",
                    po_number: po.po_number ?? "",
                    action: pendingAction,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            toast.success(t("pos_action_done", { count: String(data.updated ?? 0) }));
            setPendingAction(null);
            await loadLines();
        } catch {
            toast.error(t("server_error"), { toastId: "pos-action-err" });
        } finally {
            setActing(false);
        }
    };

    // The "" (all lines) pill comes from FilterPills itself.
    const lineFilterOptions = [
        { value: "outstanding", label: t("pos_lines_filter_outstanding") },
        { value: "complete", label: t("pos_lines_filter_complete") },
    ];

    return (
        <div className="inventory-admin po-admin">
            {/* Trail pills — one per step taken so far. */}
            <div className="po-admin-steps filter-pills warehouse-admin-pills">
                <StepPill
                    label={t("pos_crumb_suppliers")}
                    current={!supplierSelected}
                    onClick={goSuppliers}
                />
                {supplierSelected && (
                    <>
                        <ChevronRight size={14} className="po-admin-steps-sep" />
                        <StepPill
                            label={supplierLabel(supplier)}
                            current={!poSelected}
                            onClick={() => goSupplier(supplier)}
                        />
                    </>
                )}
                {poSelected && (
                    <>
                        <ChevronRight size={14} className="po-admin-steps-sep" />
                        <StepPill
                            label={poLabel(poNumber)}
                            current
                            onClick={() => undefined}
                        />
                    </>
                )}
                <div className="filter-pills-trailing">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={refresh}
                        disabled={loading}
                    >
                        <RefreshCw size={14} /> {t("refresh")}
                    </button>
                </div>
            </div>

            {/* Step 1 — suppliers with PO counts */}
            {!supplierSelected && (
                <div className="locations-table-wrap">
                    <table className="locations-table po-admin-table">
                        <thead>
                            <tr>
                                <th>{t("inventory_col_supplier")}</th>
                                <th className="locations-table-num">{t("pos_col_active_pos")}</th>
                                <th className="locations-table-num">{t("pos_col_total_pos")}</th>
                                <th className="locations-table-num">{t("pos_col_open_lines")}</th>
                                <th className="locations-table-num">{t("manifest_col_expected_qty")}</th>
                                <th className="locations-table-num">{t("manifest_col_received")}</th>
                                <th>{t("pos_col_updated")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {suppliers.map(s => (
                                <tr
                                    key={s.supplier_name ?? "∅"}
                                    className="po-admin-row-link"
                                    onClick={() => goSupplier(s.supplier_name)}
                                >
                                    <td className="po-admin-name">{supplierLabel(s.supplier_name)}</td>
                                    <td className="locations-table-num">{s.active_pos}</td>
                                    <td className="locations-table-num">{s.total_pos}</td>
                                    <td className="locations-table-num">{s.open_lines}</td>
                                    <td className="locations-table-num">{s.expected_qty}</td>
                                    <td className="locations-table-num">{s.received_qty}</td>
                                    <td>{s.updated_at ? formatDate(s.updated_at) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!loading && suppliers.length === 0 && (
                        <div className="locations-table-empty">{t("pos_no_suppliers")}</div>
                    )}
                </div>
            )}

            {/* Step 2 — POs for one supplier */}
            {supplierSelected && !poSelected && (
                <>
                    {/* FilterPills always renders a leading "" pill; here
                        that pill is the default active-only view and the
                        single option flips to show-all. */}
                    <FilterPills
                        className="warehouse-admin-pills"
                        value={showAll ? "all" : ""}
                        allLabelKey="pos_active_only"
                        onChange={v => goSupplier(supplier, v === "all")}
                        options={[{ value: "all", label: t("pos_show_all") }]}
                    />
                    <div className="locations-table-wrap">
                        <table className="locations-table po-admin-table">
                            <thead>
                                <tr>
                                    <th>{t("inventory_col_po")}</th>
                                    <th className="locations-table-num">{t("pos_col_lines")}</th>
                                    <th className="locations-table-num">{t("manifest_col_expected_qty")}</th>
                                    <th className="locations-table-num">{t("manifest_col_received")}</th>
                                    <th>{t("pos_col_progress")}</th>
                                    <th>{t("manifest_col_manifest_date")}</th>
                                    <th>{t("manifest_col_expected_at")}</th>
                                    <th>{t("manifest_col_status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pos.map(p => (
                                    <tr
                                        key={p.po_number ?? "∅"}
                                        className="po-admin-row-link"
                                        onClick={() => goPo(p.supplier_name, p.po_number)}
                                    >
                                        <td className="po-admin-name locations-table-mono">{poLabel(p.po_number)}</td>
                                        <td className="locations-table-num">{p.complete_lines} / {p.lines}</td>
                                        <td className="locations-table-num">{p.expected_qty}</td>
                                        <td className="locations-table-num">{p.received_qty}</td>
                                        <td><Progress received={p.received_qty} expected={p.expected_qty} /></td>
                                        <td>{p.manifest_date ? formatDate(p.manifest_date) : "—"}</td>
                                        <td>{p.expected_at ? formatDate(p.expected_at) : "—"}</td>
                                        <td><StatusPill status={p.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!loading && pos.length === 0 && (
                            <div className="locations-table-empty">{t("pos_no_pos")}</div>
                        )}
                    </div>
                </>
            )}

            {/* Step 3 — one PO: summary, actions, line match-up */}
            {poSelected && po && (
                <>
                    <div className="po-admin-summary">
                        <div className="po-admin-summary-main">
                            <div className="po-admin-summary-title">
                                <span className="locations-table-mono">{poLabel(po.po_number)}</span>
                                <StatusPill status={po.status} />
                            </div>
                            <div className="po-admin-summary-sub">
                                {supplierLabel(po.supplier_name)}
                                {po.manifest_date && <> · {t("manifest_col_manifest_date")}: {formatDate(po.manifest_date)}</>}
                                {po.expected_at && <> · {t("manifest_col_expected_at")}: {formatDate(po.expected_at)}</>}
                            </div>
                            <div className="po-admin-summary-stats">
                                <span>{t("pos_summary_lines", { done: String(po.complete_lines), total: String(po.lines) })}</span>
                                <span>{t("pos_summary_units", { received: String(po.received_qty), expected: String(po.expected_qty) })}</span>
                            </div>
                            <Progress received={po.received_qty} expected={po.expected_qty} />
                        </div>
                        <div className="po-admin-summary-actions">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={acting || po.open_lines === 0 || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={() => setPendingAction("close_short")}
                            >
                                <CheckCircle2 size={14} /> {t("pos_action_close_short")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={acting || reopenable <= 0 || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={() => setPendingAction("reopen")}
                            >
                                <RotateCcw size={14} /> {t("pos_action_reopen")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                disabled={acting || po.open_lines === 0 || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                                onClick={() => setPendingAction("cancel")}
                            >
                                <Ban size={14} /> {t("pos_action_cancel")}
                            </button>
                        </div>
                    </div>

                    <FilterPills
                        className="warehouse-admin-pills"
                        value={lineFilter}
                        allLabelKey="pos_lines_filter_all"
                        onChange={v => setLineFilter(v as LineFilter)}
                        options={lineFilterOptions}
                    />
                    <div className="warehouse-admin-filters users-admin-filters">
                        <input
                            className="locations-table-search wh-admin-filter-search"
                            type="search"
                            placeholder={t("pos_lines_search_hint")}
                            value={lineSearch}
                            onChange={e => setLineSearch(e.target.value)}
                        />
                    </div>

                    <div className="locations-table-wrap">
                        <table className="locations-table po-admin-table">
                            <thead>
                                <tr>
                                    <th className="locations-table-num">{t("manifest_col_id")}</th>
                                    <th>{t("inventory_col_sku")}</th>
                                    <th>{t("inventory_col_warehouse")}</th>
                                    <th className="locations-table-num">{t("manifest_col_received")}</th>
                                    <th className="locations-table-num">{t("inventory_col_unit_cost")}</th>
                                    <th>{t("inventory_col_supplier")}</th>
                                    <th>{t("inventory_col_po")}</th>
                                    <th>{t("manifest_col_manifest_date")}</th>
                                    <th>{t("manifest_col_expected_at")}</th>
                                    <th>{t("manifest_col_status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleLines.map(r => {
                                    const overage = r.received_qty - r.expected_qty;
                                    const complete = r.received_qty >= r.expected_qty;
                                    return (
                                        <tr
                                            key={r.manifest_id}
                                            className={complete ? "po-admin-line--complete" : undefined}
                                            data-complete={complete ? "1" : "0"}
                                        >
                                            <td className="locations-table-num locations-table-mono">{r.manifest_id}</td>
                                            <td className="locations-table-mono">{r.sku}</td>
                                            <td>{r.warehouse}</td>
                                            <td className="locations-table-num">
                                                {r.received_qty} / {r.expected_qty}
                                                {overage > 0 && (
                                                    <span className="manifest-received-overage">+{overage}</span>
                                                )}
                                            </td>
                                            <td className="locations-table-num">{formatMoney(r.unit_cost, r.currency)}</td>
                                            <td>{r.supplier_name ?? "—"}</td>
                                            <td>{r.po_number ?? "—"}</td>
                                            <td>{r.manifest_date ? formatDate(r.manifest_date) : "—"}</td>
                                            <td>{r.expected_at ? formatDate(r.expected_at) : "—"}</td>
                                            <td><StatusPill status={r.status} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {!loading && visibleLines.length === 0 && (
                            <div className="locations-table-empty">{t("pos_no_lines")}</div>
                        )}
                    </div>
                    <div className="locations-table-footer">
                        <span>{t("pos_lines_showing", { shown: String(visibleLines.length), total: String(lines.length) })}</span>
                    </div>
                </>
            )}

            {pendingAction && po && (
                <div className="modal-overlay" onClick={() => !acting && setPendingAction(null)}>
                    <div
                        className="modal-card po-admin-modal"
                        onClick={e => e.stopPropagation()}
                        role="dialog"
                        aria-labelledby="po-action-title"
                    >
                        <div className="modal-card-header">
                            <h2 id="po-action-title" className="products-admin-modal-title">
                                {t(`pos_confirm_${pendingAction}_title`)}
                            </h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setPendingAction(null)}
                                disabled={acting}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p>
                                {t(`pos_confirm_${pendingAction}_body`, {
                                    po: poLabel(po.po_number),
                                    count: String(actionCount(pendingAction)),
                                })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => setPendingAction(null)}
                                    disabled={acting}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${pendingAction === "cancel" ? "btn-danger" : "btn-primary"}`}
                                    onClick={runAction}
                                    disabled={acting || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {acting ? t("submitting") : t(`pos_action_${pendingAction}`)}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PosAdmin;
